import { useEffect, useRef } from "react";
import type { MealMonitoringDetailTab } from "../../../../../api/meal/types/mealMonitoringDetailType";

interface MealDetailTabBarProps {
  tabs: MealMonitoringDetailTab[];
  activeTab: string;
  onChange: (title: string) => void;
}

const MealDetailTabBar = ({
  tabs,
  activeTab,
  onChange,
}: MealDetailTabBarProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  useEffect(() => {
    const container = containerRef.current;
    const activeButton = tabRefs.current[activeTab];

    if (!container || !activeButton) {
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const buttonRect = activeButton.getBoundingClientRect();
    const currentScrollLeft = container.scrollLeft;
    const buttonCenter = buttonRect.left + buttonRect.width / 2;
    const containerCenter = containerRect.left + containerRect.width / 2;
    const offset = buttonCenter - containerCenter;
    const maxScroll = container.scrollWidth - container.clientWidth;
    const nextScrollLeft = Math.min(
      Math.max(currentScrollLeft + offset, 0),
      Math.max(maxScroll, 0),
    );

    container.scrollTo({
      left: nextScrollLeft,
      behavior: "smooth",
    });
  }, [activeTab]);

  return (
    <div
      ref={containerRef}
      className="flex w-full gap-6 overflow-x-auto border-b border-gray-100 px-1"
      style={{
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        WebkitOverflowScrolling: "touch",
      }}
    >
      {tabs.map((tab) => {
        const isActive = activeTab === tab.title;
        const total = tab.items?.length ?? 0;

        return (
          <button
            key={tab.title}
            ref={(element) => {
              tabRefs.current[tab.title] = element;
            }}
            type="button"
            onClick={() => onChange(tab.title)}
            className={`relative flex shrink-0 items-center gap-1.5 whitespace-nowrap pb-2.5 pt-1 text-[13px] font-semibold transition-colors duration-200 ${
              isActive ? "text-[#ff706b]" : "text-[#999]"
            }`}
          >
            <span>{tab.title}</span>

            <span
              className={`text-[11px] font-bold ${
                isActive ? "text-[#ff706b]" : "text-[#b5b5b5]"
              }`}
            >
              ({total})
            </span>

            <span
              className={`absolute bottom-0 left-0 right-0 h-[2px] rounded-full bg-[#ff706b] transition-transform duration-200 ${
                isActive ? "scale-x-100" : "scale-x-0"
              }`}
            />
          </button>
        );
      })}
    </div>
  );
};

export default MealDetailTabBar;
